import React from "react";

import {
  ChevronDown,
  ChevronUp,
  CircleCheckBig,
  Pencil,
  Trash2,
} from "lucide-react";

import { Button } from "@/src/components/ui/button";
import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";
import { cn } from "@/src/lib/utils";

import DataTableColumnHeader from "../../../components/dataTable/DataTableColumnHeader";
import {
  actionsColumn,
  enabledStatusOptions,
  statusColumn,
} from "../../../components/dataTable/dataTableColumns";
import DataTableLinkCell from "../../../components/dataTable/DataTableLinkCell";
import { CELL_ICON_CLASS } from "../../../components/dataTable/dataTableStyles";
import { PROJECTS_ROUTE } from "../../../components/routes/routes";

import type { TFunction } from "i18next";

import type { RankChange } from "./projectPageColumns.types";
import type { ProjectPage } from "../../../api/ProjectAPI.types";
import type { DataTableColumnDef } from "../../../components/dataTable/dataTable.types";

interface ProjectPageColumnsOptions {
  t: TFunction;
  projectSlug: string;
  pageCount: number;
  isRankLoading: boolean;
  onRankChange: (page: ProjectPage, rankChange: RankChange) => void;
  onSetDefault: (page: ProjectPage) => void;
  onEdit: (page: ProjectPage) => void;
  onDelete: (page: ProjectPage) => void;
}

export function getProjectPageHref(projectSlug: string, uuid: string): string {
  const layoutHref = `${PROJECTS_ROUTE}/${projectSlug}/layout`;
  return uuid ? `${layoutHref}/pages/${uuid}/web` : layoutHref;
}

const RANK_BUTTON_CLASS =
  "size-4 text-muted-foreground hover:bg-transparent hover:text-primary dark:hover:bg-transparent";

export function projectPageColumns({
  t,
  projectSlug,
  pageCount,
  isRankLoading,
  onRankChange,
  onSetDefault,
  onEdit,
  onDelete,
}: ProjectPageColumnsOptions): DataTableColumnDef<ProjectPage>[] {
  const status = statusColumn<ProjectPage>(
    "enabled",
    t("pages.status"),
    enabledStatusOptions(t),
  );

  return [
    {
      ...status,
      accessorFn: (page) => (page.enabled ? "enabled" : "disabled"),
      meta: { ...status.meta, filterOptions: undefined },
    },
    {
      id: "rank",
      accessorFn: (page) => page.rank,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("pages.rank")} />
      ),
      enableSorting: false,
      meta: {
        label: t("pages.rank"),
        width: "27%",
        minWidth: 170,
        align: "center",
      },
      cell: ({ row }) => {
        const page = row.original;

        return (
          <div className="flex items-center justify-center gap-2">
            {page.rank + 1}
            {pageCount > 1 ? (
              <div className="flex flex-col">
                <Button
                  aria-label={t("pages.moveUp")}
                  className={RANK_BUTTON_CLASS}
                  disabled={isRankLoading || page.rank === 0}
                  onClick={() => onRankChange(page, -1)}
                  size="icon-xs"
                  type="button"
                  variant="ghost"
                >
                  <ChevronUp aria-hidden="true" className="size-4" />
                </Button>
                <Button
                  aria-label={t("pages.moveDown")}
                  className={RANK_BUTTON_CLASS}
                  disabled={isRankLoading || page.rank >= pageCount - 1}
                  onClick={() => onRankChange(page, 1)}
                  size="icon-xs"
                  type="button"
                  variant="ghost"
                >
                  <ChevronDown aria-hidden="true" className="size-4" />
                </Button>
              </div>
            ) : null}
          </div>
        );
      },
    },
    {
      id: "label",
      accessorKey: "label",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("pages.name")} />
      ),
      enableSorting: false,
      meta: { label: t("pages.name") },
      cell: ({ row }) => (
        <DataTableLinkCell
          href={getProjectPageHref(projectSlug, row.original.uuid)}
        >
          {row.original.label}
        </DataTableLinkCell>
      ),
    },
    {
      id: "default",
      accessorFn: (page) => page.default,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("pages.default")} />
      ),
      enableSorting: false,
      meta: {
        label: t("pages.default"),
        width: "35%",
        minWidth: 135,
        align: "center",
      },
      cell: ({ row }) =>
        row.original.default ? (
          <span className="inline-flex align-middle">
            <CircleCheckBig
              aria-hidden="true"
              className={cn(CELL_ICON_CLASS, "text-primary")}
            />
            <span className="sr-only">{t("pages.default")}</span>
          </span>
        ) : (
          // Un bouton désactivé laisse passer le clic à la ligne, qui ouvrirait la page.
          <span
            className="inline-flex"
            data-tooltip-id={APP_TOOLTIP_ID}
            onClick={(event) => event.stopPropagation()}
            data-tooltip-content={
              row.original.enabled ? undefined : t("pages.enableBeforeHomepage")
            }
          >
            <Button
              aria-label={t("pages.changeHomepage")}
              className="text-subtle hover:text-muted-foreground"
              disabled={!row.original.enabled}
              onClick={() => onSetDefault(row.original)}
              size="icon-sm"
              type="button"
              variant="ghost"
            >
              <CircleCheckBig aria-hidden="true" className="size-5" />
            </Button>
          </span>
        ),
    },
    actionsColumn<ProjectPage>(
      (page) => [
        {
          label: t("pages.edit"),
          icon: <Pencil />,
          onClick: () => onEdit(page),
        },
        {
          label: t("pages.delete"),
          icon: <Trash2 />,
          variant: "destructive",
          disabled: page.default,
          onClick: () => onDelete(page),
          confirm: {
            title: t("pages.deleteModalTitle"),
            description: t("pages.deleteModalText", { pageName: page.label }),
            confirmLabel: t("pages.delete"),
          },
        },
      ],
      (page) => page.label,
    ),
  ];
}
